import { useEffect, useMemo, useReducer, useState } from "react";
import api from "@/api";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/index";
import { AlertCircle, ArrowLeft, ArrowRight, CheckCircle, ClipboardList, Send } from "lucide-react";
import { PatientSpeakerButton, usePatientAudioCache } from "@/components/shared/PatientSpeakerButton";

const COPY = {
  fr: {
    loading: "Chargement...",
    notFound: "Questionnaire introuvable",
    loadError: "Erreur chargement du questionnaire",
    sendError: "Erreur lors de l'envoi",
    visit: "Visite",
    question: "Question",
    of: "sur",
    answered: "répondues",
    missing: "Il reste des questions sans réponse",
    recall: "Ces 7 derniers jours",
    previous: "Précédent",
    next: "Question suivante",
    finish: "Envoyer mes réponses",
    sending: "Envoi...",
    complete: "Toutes les questions sont répondues",
    choose: "Choisissez une réponse pour continuer",
    score: "Votre réponse",
    sliderMin: "Le moins",
    sliderMax: "Le plus",
    attrs: { F: "Fréquence", S: "Sévérité", I: "Impact sur la vie quotidienne", A: "Quantité" },
  },
  ar: {
    loading: "جارٍ التحميل...",
    notFound: "تعذر العثور على الاستبيان",
    loadError: "حدث خطأ أثناء تحميل الاستبيان",
    sendError: "حدث خطأ أثناء الإرسال",
    visit: "الزيارة",
    question: "السؤال",
    of: "من",
    answered: "تمت الإجابة",
    missing: "ما زالت هناك أسئلة بدون جواب",
    recall: "خلال الأيام السبعة الماضية",
    previous: "السابق",
    next: "السؤال التالي",
    finish: "إرسال الإجابات",
    sending: "جارٍ الإرسال...",
    complete: "تمت الإجابة على كل الأسئلة",
    choose: "اختر إجابة للمتابعة",
    score: "إجابتك",
    sliderMin: "الأقل",
    sliderMax: "الأكثر",
    attrs: { F: "التكرار", S: "الشدة", I: "التأثير على الحياة اليومية", A: "الكمية" },
  },
};

export default function QuestionnaireFillForm({ enrollmentId, visitName, questionnaireCode, onCompleted }) {
  const { language } = useLanguage();
  const [activeLang, setActiveLang] = useState(language === "ar" ? "ar" : "fr");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [questionnaireState, dispatchQuestionnaire] = useReducer((current, action) => {
    switch (action.type) {
      case "loading":
        return { ...current, loading: true };
      case "loaded":
        return { ...current, loading: false, questionnaire: action.questionnaire };
      case "failed":
        return { ...current, loading: false, questionnaire: null };
      default:
        return current;
    }
  }, { questionnaire: null, loading: true });
  const [responses, dispatchResponses] = useReducer((current, action) => {
    if (action.type === "answer") return { ...current, [`q${action.itemNumber}`]: action.value };
    if (action.type === "reset") return {};
    return current;
  }, {});
  const [submitState, dispatchSubmit] = useReducer((current, action) => {
    if (action.type === "submitting") return { ...current, submitting: true };
    if (action.type === "done") return { ...current, submitting: false };
    return current;
  }, { submitting: false, startedAt: Date.now() });

  const { questionnaire, loading } = questionnaireState;
  const items = questionnaire?.items || [];
  const totalItems = items.length;
  const currentItem = items[currentIndex];
  const questionAudioKey = currentItem ? `questionnaire_${questionnaireCode}_q${currentItem.number}` : null;
  const audioCache = usePatientAudioCache(questionAudioKey ? [questionAudioKey] : []);
  const { submitting, startedAt } = submitState;
  const copy = COPY[activeLang];
  const isRTL = activeLang === "ar";

  useEffect(() => {
    dispatchResponses({ type: "reset" });
    setCurrentIndex(0);
  }, [questionnaireCode]);

  useEffect(() => {
    let cancelled = false;
    async function loadQuestionnaire() {
      try {
        dispatchQuestionnaire({ type: "loading" });
        const res = await api.get(`/studies/questionnaires/${questionnaireCode}?lang=${activeLang}`);
        if (!cancelled) dispatchQuestionnaire({ type: "loaded", questionnaire: res.data });
      } catch (err) {
        if (!cancelled) {
          toast.error(copy.loadError);
          dispatchQuestionnaire({ type: "failed" });
        }
      }
    }
    loadQuestionnaire();
    return () => { cancelled = true; };
  }, [questionnaireCode, activeLang]);

  const currentValue = currentItem ? responses[`q${currentItem.number}`] : undefined;
  const answeredCount = useMemo(
    () => Object.keys(responses).filter(k => responses[k] !== undefined && responses[k] !== null && responses[k] !== "").length,
    [responses]
  );
  const progress = totalItems > 0 ? Math.round((answeredCount / totalItems) * 100) : 0;
  const isComplete = totalItems > 0 && answeredCount === totalItems;
  const isLast = currentIndex === totalItems - 1;

  const getOptions = (item) => {
    if (!item || !questionnaire?.responseOptions) return [];
    const opts = questionnaire.responseOptions;
    const byType = opts[item.responseType];
    if (Array.isArray(byType)) return byType;
    if (byType && Array.isArray(byType[item.subscale])) return byType[item.subscale];
    if (Array.isArray(opts)) return opts.map((o, idx) => typeof o === "string" ? { value: idx + 1, label: o } : o);
    return [
      { value: 1, label: activeLang === "ar" ? "لا إطلاقا" : "Pas du tout" },
      { value: 2, label: activeLang === "ar" ? "قليلا" : "Un peu" },
      { value: 3, label: activeLang === "ar" ? "إلى حد ما" : "Assez" },
      { value: 4, label: activeLang === "ar" ? "كثيرا" : "Beaucoup" },
    ];
  };

  const getSliderConfig = (item) => {
    const cfg = questionnaire?.responseOptions?.[item?.responseType];
    if (cfg && typeof cfg === "object" && !Array.isArray(cfg) && cfg.min !== undefined) return cfg;
    return null;
  };

  const setResponse = (itemNumber, value) => dispatchResponses({ type: "answer", itemNumber, value });
  const goNext = () => {
    if (currentValue === undefined || currentValue === null || currentValue === "") {
      toast.error(copy.choose);
      return;
    }
    setCurrentIndex(idx => Math.min(idx + 1, totalItems - 1));
  };

  const handleSubmit = async () => {
    if (!isComplete) {
      toast.error(copy.missing);
      return;
    }
    try {
      dispatchSubmit({ type: "submitting" });
      const completionTimeSec = Math.round((Date.now() - startedAt) / 1000);
      await api.post(`/studies/enrollments/${enrollmentId}/responses`, {
        visitName,
        questionnaireCode,
        responses,
        completionTime: completionTimeSec,
        completedAt: new Date().toISOString(),
      });
      onCompleted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || copy.sendError);
    } finally {
      dispatchSubmit({ type: "done" });
    }
  };

  if (loading) return <div className="text-center py-16 text-slate-400" data-testid="fill-loading">{copy.loading}</div>;
  if (!questionnaire || !currentItem) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center" data-testid="questionnaire-not-found">
        <AlertCircle className="w-8 h-8 mx-auto text-red-500 mb-2" />
        <p className="text-red-700 font-medium">{copy.notFound}</p>
      </div>
    );
  }

  const options = getOptions(currentItem);
  const sliderConfig = getSliderConfig(currentItem);

  return (
    <div className="min-h-[calc(100vh-9rem)] space-y-4" data-testid="questionnaire-wizard" dir={isRTL ? "rtl" : "ltr"}>
      <div className="rounded-[1.65rem] border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-black text-slate-600" data-testid="questionnaire-visit-badge">
            <ClipboardList className="h-3.5 w-3.5" /> {copy.visit} · {visitName}
          </div>
          <div className="flex rounded-full border border-slate-200 bg-slate-50 p-0.5" data-testid="questionnaire-language-toggle">
            <button type="button" onClick={() => setActiveLang("fr")} data-testid="questionnaire-lang-fr" className={`rounded-full px-3 py-1 text-xs font-black transition-[background-color,color] ${activeLang === "fr" ? "bg-white text-[#2C5E53] shadow-sm" : "text-slate-500"}`}>FR</button>
            <button type="button" onClick={() => setActiveLang("ar")} data-testid="questionnaire-lang-ar" className={`rounded-full px-3 py-1 text-xs font-black transition-[background-color,color] ${activeLang === "ar" ? "bg-white text-[#2C5E53] shadow-sm" : "text-slate-500"}`}>AR</button>
          </div>
        </div>
        <h2 className="text-lg font-black leading-tight text-slate-900" data-testid="questionnaire-name">{questionnaire.name}</h2>
        {questionnaire.description ? <p className="mt-2 text-sm leading-relaxed text-slate-500" data-testid="questionnaire-description">{questionnaire.description}</p> : null}
        {questionnaire.recallPeriodDays ? (
          <div className="mt-3 inline-flex rounded-full bg-amber-50 px-3 py-1 text-xs font-bold text-amber-800" data-testid="questionnaire-recall-period">
            {copy.recall}
          </div>
        ) : null}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm" data-testid="questionnaire-progress-block">
        <div className="mb-2 flex items-center justify-between text-xs font-bold text-slate-500">
          <span data-testid="questionnaire-progress-count">{copy.question} {currentIndex + 1} {copy.of} {totalItems}</span>
          <span data-testid="questionnaire-progress-pct">{progress}%</span>
        </div>
        <div className="h-2.5 overflow-hidden rounded-full bg-slate-100">
          <div className="h-full rounded-full bg-[#2C5E53] transition-[width] duration-200" style={{ width: `${progress}%` }} />
        </div>
      </div>

      <div className="rounded-[1.75rem] border border-[#2C5E53]/12 bg-[#F8FAFC] p-4 shadow-[0_12px_30px_rgb(15,23,42,0.06)]" data-testid={`wizard-question-card-q${currentItem.number}`}>
        {currentItem.attribute ? (
          <span className="mb-3 inline-flex rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-black text-slate-500" data-testid="wizard-question-attribute">
            {copy.attrs[currentItem.attribute] || currentItem.attribute}
          </span>
        ) : null}
        <div className="flex items-start gap-3">
          <p className="min-w-0 flex-1 text-xl font-black leading-snug text-slate-900" data-testid="wizard-question-text">
            {currentItem.text}
          </p>
          <PatientSpeakerButton
            audioKey={questionAudioKey}
            audioCache={audioCache}
            fallbackText={currentItem.text}
            size="sm"
            testId="wizard-question-speaker"
          />
        </div>
        <div className="mt-5 space-y-2" data-testid="wizard-answer-options">
          {sliderConfig ? (
            <SliderAnswer item={currentItem} value={currentValue} onChange={setResponse} config={sliderConfig} copy={copy} />
          ) : (
            options.map((opt, idx) => (
              <button
                key={`opt-${currentItem.number}-${opt.value}`}
                type="button"
                onClick={() => setResponse(currentItem.number, opt.value)}
                data-testid={`answer-option-${idx}`}
                className={`flex min-h-[54px] w-full items-center gap-3 rounded-2xl border px-4 py-3 text-start shadow-sm transition-[transform,background-color,border-color] active:scale-[0.99] ${currentValue === opt.value ? "border-[#2C5E53] bg-[#E9F0EE] text-[#19483E]" : "border-slate-200 bg-white text-slate-700 hover:border-[#2C5E53]/30"}`}
              >
                <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm font-black ${currentValue === opt.value ? "bg-[#2C5E53] text-white" : "bg-slate-100 text-slate-500"}`} data-testid={`answer-option-${idx}-value`}>{opt.value}</span>
                <span className="text-sm font-bold leading-snug" data-testid={`answer-option-${idx}-label`}>{opt.label}</span>
              </button>
            ))
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm" data-testid="questionnaire-navigation">
        <div className="mb-3 text-center text-xs font-bold text-slate-500" data-testid="questionnaire-answered-count">
          {answeredCount} / {totalItems} {copy.answered}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={() => setCurrentIndex(idx => Math.max(idx - 1, 0))} disabled={currentIndex === 0} data-testid="wizard-prev-button" className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-black text-slate-700 transition-[background-color,opacity] hover:bg-slate-50 disabled:opacity-35">
            <ArrowLeft className="h-4 w-4" /> {copy.previous}
          </button>
          {isLast ? (
            <button type="button" onClick={handleSubmit} disabled={!isComplete || submitting} data-testid="submit-questionnaire-btn" className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl bg-[#2C5E53] px-4 text-sm font-black text-white transition-[background-color,opacity] hover:bg-[#214D44] disabled:opacity-40">
              {submitting ? copy.sending : copy.finish} <Send className="h-4 w-4" />
            </button>
          ) : (
            <button type="button" onClick={goNext} disabled={currentValue === undefined || currentValue === null || currentValue === ""} data-testid="wizard-next-button" className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl bg-[#2C5E53] px-4 text-sm font-black text-white transition-[background-color,opacity] hover:bg-[#214D44] disabled:opacity-40">
              {copy.next} <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
        {isComplete ? (
          <p className="mt-3 flex items-center justify-center gap-1.5 text-xs font-bold text-emerald-700" data-testid="questionnaire-complete-message">
            <CheckCircle className="h-4 w-4" /> {copy.complete}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function SliderAnswer({ item, value, onChange, config, copy }) {
  const current = value ?? config.min ?? 0;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="slider-answer-block">
      <div className="mb-3 flex items-center justify-between text-xs font-bold text-slate-500">
        <span>{copy.sliderMin}</span>
        <span>{copy.sliderMax}</span>
      </div>
      <input
        type="range"
        min={config.min ?? 0}
        max={config.max ?? 100}
        step={config.step ?? 1}
        value={current}
        onChange={(e) => onChange(item.number, Number(e.target.value))}
        data-testid="slider-answer-input"
        className="w-full accent-[#2C5E53]"
      />
      <p className="mt-3 text-center text-sm font-black text-[#2C5E53]" data-testid="slider-answer-value">{copy.score} : {current}</p>
    </div>
  );
}